/**
 * @lodex-pro/web-portal/hooks/useOperatorPortal — TanStack Query hooks for the
 * operator portal API surface (DEC-153 / Task 15.4).
 *
 * Mirrors the web-admin `features/operator-registrations/hooks.ts` pattern:
 * each hook wraps `useApiQuery` from `@lodex-pro/web-shared`, uses `useApiOpts`
 * for the api-client, and keys the query on a stable tuple.
 *
 * Response types are `import type` only (DEC-149 §9 — web packages never import
 * Zod schemas client-side; only the derived TS interface).
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiErrorFromResponse, authedFetch, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type { OperatorPortalMeResponse } from '@lodex-pro/shared/validation/operator-portal-me';
import type {
  ListPortalTicketsResponse,
  PortalTicketDetailResponse,
} from '@lodex-pro/shared/validation/operator-portal-tickets';
import type { PortalVolumesResponse } from '@lodex-pro/shared/validation/operator-portal-volumes';
import type { PortalTanksResponse } from '@lodex-pro/shared/validation/operator-portal-tanks';
// Phase 18 / Task 18.9 (OPAPI-06) — the operator's own API usage.
import type { PortalApiUsageResponse } from '@lodex-pro/shared/validation/operator-portal-api-usage';

// ── Query key builders ───────────────────────────────────────────────────────

export const PORTAL_ME_KEY: readonly string[] = ['operator-portal', 'me'];
export const PORTAL_TICKETS_KEY = (params: TicketsQuery): readonly unknown[] => [
  'operator-portal',
  'tickets',
  params,
];
export const PORTAL_TICKET_KEY = (id: string): readonly string[] => [
  'operator-portal',
  'ticket',
  id,
];
export const PORTAL_VOLUMES_KEY = (params: VolumesQuery): readonly unknown[] => [
  'operator-portal',
  'volumes',
  params,
];
export const PORTAL_TANKS_KEY: readonly string[] = ['operator-portal', 'tanks'];
export const PORTAL_API_USAGE_KEY = (period: string | undefined): readonly unknown[] => [
  'operator-portal',
  'api-usage',
  // Keyed on the literal 'current' when no period is given, so switching from
  // an explicit month BACK to the current one is a distinct cache entry rather
  // than a key of `undefined` that collides with the first render.
  period ?? 'current',
];

// ── Query param types ────────────────────────────────────────────────────────

export interface TicketsQuery {
  readonly limit?: number;
  readonly offset?: number;
  readonly from?: string;
  readonly to?: string;
  readonly siteId?: string;
}

export interface VolumesQuery {
  readonly from?: string;
  readonly to?: string;
  readonly groupBy?: 'site' | 'tank' | 'month';
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

function toQueryString(params: Record<string, string | number | undefined>): string {
  const parts = Object.entries(params)
    .filter((entry): entry is [string, string | number] => entry[1] !== undefined)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

export function usePortalMe(): ReturnType<typeof useApiQuery<OperatorPortalMeResponse>> {
  const opts = useApiOpts();
  return useApiQuery<OperatorPortalMeResponse>(opts, '/v1/operator-portal/me', {
    queryKey: PORTAL_ME_KEY,
    staleTime: 60_000,
  });
}

export function usePortalTickets(
  params: TicketsQuery = {},
): ReturnType<typeof useApiQuery<ListPortalTicketsResponse>> {
  const opts = useApiOpts();
  const qs = toQueryString({
    limit: params.limit,
    offset: params.offset,
    from: params.from,
    to: params.to,
    siteId: params.siteId,
  });
  return useApiQuery<ListPortalTicketsResponse>(opts, `/v1/operator-portal/tickets${qs}`, {
    queryKey: PORTAL_TICKETS_KEY(params),
    staleTime: 30_000,
  });
}

export function usePortalTicket(
  id: string,
): ReturnType<typeof useApiQuery<PortalTicketDetailResponse>> {
  const opts = useApiOpts();
  return useApiQuery<PortalTicketDetailResponse>(opts, `/v1/operator-portal/tickets/${id}`, {
    queryKey: PORTAL_TICKET_KEY(id),
    staleTime: 30_000,
    enabled: id.length > 0,
  });
}

/** GET /v1/operator-portal/tickets/:id/pdf — a short-lived presigned link to the run ticket. */
export interface PortalTicketPdfLink {
  readonly haulId: string;
  readonly filename: string;
  readonly url: string;
  readonly expiresInSeconds: number;
}

/**
 * Operator Reports (0241) — render the ticket's run-ticket PDF and get a link to it. Called on
 * a click, never on mount: each render is a renderer run. Errors carry the API code
 * (OPERATOR_TICKET_PDF_UNAVAILABLE, RATE_LIMIT_EXCEEDED).
 */
export function usePortalTicketPdf(
  id: string,
): UseMutationResult<PortalTicketPdfLink, Error, void> {
  const opts = useApiOpts();
  return useMutation<PortalTicketPdfLink, Error, void>({
    mutationFn: async (): Promise<PortalTicketPdfLink> => {
      const path = `/v1/operator-portal/tickets/${encodeURIComponent(id)}/pdf`;
      const res = await authedFetch(opts, path);
      if (!res.ok) {
        throw await apiErrorFromResponse(res, path);
      }
      return (await res.json()) as PortalTicketPdfLink;
    },
  });
}

export function usePortalVolumes(
  params: VolumesQuery = {},
): ReturnType<typeof useApiQuery<PortalVolumesResponse>> {
  const opts = useApiOpts();
  const qs = toQueryString({
    from: params.from,
    to: params.to,
    groupBy: params.groupBy,
  });
  return useApiQuery<PortalVolumesResponse>(opts, `/v1/operator-portal/volumes${qs}`, {
    queryKey: PORTAL_VOLUMES_KEY(params),
    staleTime: 60_000,
  });
}

export function usePortalTanks(): ReturnType<typeof useApiQuery<PortalTanksResponse>> {
  const opts = useApiOpts();
  return useApiQuery<PortalTanksResponse>(opts, '/v1/operator-portal/tanks', {
    queryKey: PORTAL_TANKS_KEY,
    staleTime: 60_000,
  });
}

/**
 * The operator's own API usage for one UTC month (Task 18.9 / OPAPI-06).
 *
 * `period` is `YYYY-MM`; omitted means the current month, and the response
 * echoes which month it actually reported so the UI never has to guess.
 *
 * staleTime is deliberately SHORT relative to the other portal reads. Those are
 * historical aggregates that barely move; this is a live counter an operator may
 * be watching while wiring up an integration, and a minute-stale "you have made
 * 0 calls" is the one answer this screen must not give.
 */
export function usePortalApiUsage(
  period?: string,
): ReturnType<typeof useApiQuery<PortalApiUsageResponse>> {
  const opts = useApiOpts();
  const qs = toQueryString({ period });
  return useApiQuery<PortalApiUsageResponse>(opts, `/v1/operator-portal/api-usage${qs}`, {
    queryKey: PORTAL_API_USAGE_KEY(period),
    staleTime: 10_000,
  });
}

/** The spreadsheet download of a portal page (operator Reports (a), migration 0241). */
export interface PortalExportFile {
  readonly filename: string;
  readonly blob: Blob;
}

/**
 * GET /v1/operator-portal/{tickets|volumes}/export with the page's own query and a format. A
 * fetch, not a link: the API needs the bearer token. Errors carry the API code
 * (OPERATOR_REPORT_TOO_LARGE, FEATURE_NOT_ENABLED).
 */
export function usePortalExport(
  page: 'tickets' | 'volumes',
): UseMutationResult<PortalExportFile, Error, Record<string, string | undefined>> {
  const opts = useApiOpts();
  return useMutation<PortalExportFile, Error, Record<string, string | undefined>>({
    mutationFn: async (params): Promise<PortalExportFile> => {
      const path = `/v1/operator-portal/${page}/export${toQueryString(params)}`;
      const res = await authedFetch(opts, path);
      if (!res.ok) {
        throw await apiErrorFromResponse(res, path);
      }
      const disposition = res.headers.get('content-disposition') ?? '';
      const match = /filename="([^"]+)"/.exec(disposition);
      return { filename: match?.[1] ?? `lodex-${page}`, blob: await res.blob() };
    },
  });
}

/** The statement's query (operator Reports (c), migration 0256): local days, both included. */
export interface PortalStatementParams {
  readonly from: string;
  readonly to: string;
  readonly timeZone: string;
  readonly siteId?: string;
  readonly runTickets: boolean;
}

/** GET /v1/operator-portal/reports/statement — a short-lived link to the stored PDF. */
export interface PortalStatementLink {
  readonly filename: string;
  readonly url: string;
  readonly expiresInSeconds: number;
  readonly ticketCount: number;
}

/**
 * Operator Reports (c) — render the period statement and get a link to it. Called on a click,
 * never on mount: each one is a renderer run.
 */
export function usePortalStatement(): UseMutationResult<
  PortalStatementLink,
  Error,
  PortalStatementParams
> {
  const opts = useApiOpts();
  return useMutation<PortalStatementLink, Error, PortalStatementParams>({
    mutationFn: async (p): Promise<PortalStatementLink> => {
      const path = `/v1/operator-portal/reports/statement${toQueryString({
        from: p.from,
        to: p.to,
        timeZone: p.timeZone,
        siteId: p.siteId,
        runTickets: p.runTickets ? 'true' : 'false',
      })}`;
      const res = await authedFetch(opts, path);
      if (!res.ok) {
        // On OPERATOR_REPORT_TOO_LARGE the field says what to change: `runTickets` (too many run
        // ticket pages) or `from` (too many tickets).
        throw await apiErrorFromResponse(res, path);
      }
      return (await res.json()) as PortalStatementLink;
    },
  });
}
